import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Loader2, Plus, X } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import SpotifySongSearch from '@/components/common/SpotifySongSearch';
import { SpotifyTrack } from '@/services/spotifyService';
import { useAuth } from '@/contexts/AuthContext';
import { upsertDisplayYear, upsertUserCollectionYear } from '@/utils/displayYearUpsert';
import { createVideoFromDisplaySong } from '@/utils/autoVideoCreation';

interface AddSongFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSongAdded: (song: Song) => void;
  displayId: string;
  editingSong?: Song | null;
}

interface DisplayYear {
  id: string;
  display_id: string;
  year: number;
  description?: string;
}

interface DisplaySong {
  id: string;
  display_year_id: string;
  title: string;
  artist: string;
  year_introduced: number;
  duration?: string;
  genre?: string;
  sequence_available: boolean;
  sequence_file_url: string | null;
  sequence_price: number | null;
  created_at: string;
  updated_at: string;
  album_cover_url: string | null;
}

// Local Song interface that matches the component's usage
interface Song {
  id: string;
  title: string;
  artist: string;
  duration: string; // This component expects string format like "3:45"
  year: number;
  genre?: string;
  albumCover?: string;
  album_cover_url?: string; // Database field name fallback
  videoUrl?: string;
}

const formSchema = z.object({
  title: z.string().min(1, 'Song title is required'),
  artist: z.string().min(1, 'Artist is required'),
  duration: z.string().min(1, 'Duration is required'),
  genre: z.string().optional(),
  year: z.number().min(1900, 'Year must be after 1900').max(new Date().getFullYear(), 'Year cannot be in the future'),
  videoUrl: z.string().optional().refine((url) => {
    if (!url || url.trim() === '') return true; // Optional field, empty is valid
    
    // Video URL validation regex for multiple platforms
    const videoUrlRegex = /^https?:\/\/(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/)|youtu\.be\/|vimeo\.com\/|facebook\.com\/.*\/videos\/|fb\.watch\/|dailymotion\.com\/video\/|twitch\.tv\/videos\/|tiktok\.com\/.+\/video\/|instagram\.com\/(p|reel)\/)/i;
    
    return videoUrlRegex.test(url.trim());
  }, {
    message: 'Please enter a valid video URL from YouTube, Vimeo, Facebook, TikTok, Instagram, Twitch, or Dailymotion'
  })
});

type FormValues = z.infer<typeof formSchema> & {
  albumCover?: File;
};

const AddSongForm: React.FC<AddSongFormProps> = ({ isOpen, onClose, onSongAdded, displayId, editingSong }) => {
  const { user } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedSpotifyTrack, setSelectedSpotifyTrack] = useState<SpotifyTrack | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      artist: '',
      duration: '',
      genre: '',
      year: new Date().getFullYear(),
      videoUrl: '',
    },
  });

  // Reset form when opening/closing dialog or changing songs
  useEffect(() => {
    if (isOpen) {
    if (editingSong) {
      form.reset({
        title: editingSong.title,
        artist: editingSong.artist,
        duration: editingSong.duration,
        genre: editingSong.genre || '',
        year: editingSong.year,
        videoUrl: editingSong.videoUrl || '',
      });
        // Handle both albumCover and album_cover_url field names
        const existingAlbumCover = editingSong.albumCover || editingSong.album_cover_url;
        if (existingAlbumCover) {
          setPreviewUrl(existingAlbumCover);
        }
    } else {
      form.reset({
        title: '',
        artist: '',
        duration: '',
        genre: '',
        year: new Date().getFullYear(),
        videoUrl: '',
      });
      setPreviewUrl(null);
        setSelectedSpotifyTrack(null);
      }
    }
  }, [isOpen, editingSong, form]);

  const handleSpotifyTrackSelect = (trackData: {
    title: string;
    artist: string;
    album?: string;
    duration?: string;
    albumCoverUrl?: string;
  }) => {
    // Auto-populate form fields with Spotify data
    form.setValue('title', trackData.title);
    form.setValue('artist', trackData.artist);
    if (trackData.duration) {
      form.setValue('duration', trackData.duration);
    }
    if (trackData.album) {
      form.setValue('genre', trackData.album); // Use album as genre fallback
    }

    // Set album cover preview
    if (trackData.albumCoverUrl) {
      setPreviewUrl(trackData.albumCoverUrl);
    }
  };

  const handleSpotifyTrackClear = () => {
    setSelectedSpotifyTrack(null);
    // Don't clear form fields - let user decide if they want to keep them
  };

  const uploadAlbumCover = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('album-covers')
      .upload(filePath, file);

    if (uploadError) {
      throw uploadError;
    }

      const { data } = supabase.storage
        .from('album-covers')
      .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading album cover:', error);
      toast({
        variant: "destructive",
        title: "Upload Error",
        description: "Failed to upload album cover. Song will be saved without cover."
      });
      return null;
    }
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsUploading(true);

      // Ensure user is authenticated
      if (!user) {
        toast({
          variant: "destructive",
          title: "Authentication Required",
          description: "You must be logged in to add songs."
        });
        onClose();
        return;
      }

      // Check and refresh session if needed
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        console.error('Session error:', sessionError);
        toast({
          variant: "destructive",
          title: "Session Expired",
          description: "Your session has expired. Please refresh the page and try again."
        });
        onClose();
        return;
      }

      // Debug: Log session info
      console.log('Current session:', {
        userId: session.user.id,
        email: session.user.email,
        tokenExpiry: new Date(session.expires_at! * 1000),
        isExpired: session.expires_at! * 1000 < Date.now()
      });

      // If token is close to expiry, try to refresh
      if (session.expires_at! * 1000 - Date.now() < 60000) { // Less than 1 minute left
        console.log('Token close to expiry, refreshing...');
        const { data: { session: newSession }, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError || !newSession) {
          console.error('Failed to refresh session:', refreshError);
          toast({
            variant: "destructive",
            title: "Session Refresh Failed",
            description: "Please refresh the page and try again."
          });
          onClose();
          return;
        }
        console.log('Session refreshed successfully');
      }
      
      let displayYearId;
      
      // Handle case where user doesn't have a display yet
      if (!displayId || displayId.trim() === '') {
        // Create or get a user song collection (display_year entry with null display_id)
        try {
          console.log('Attempting to get/create user collection for:', {
            userId: user.id,
            year: values.year
          });

          const userCollectionYear = await upsertUserCollectionYear(
            user.id, 
            values.year,
            `Personal song collection for ${values.year}`
          );

          if (!userCollectionYear) {
            throw new Error('Failed to get or create user collection year');
          }

          displayYearId = userCollectionYear.id;
          console.log('Using user collection:', displayYearId);
        } catch (error) {
          console.error('Database operation failed:', error);
          
          // Handle specific authentication errors
          if (error instanceof Error && (error.message?.includes('406') || error.message?.includes('Not Acceptable'))) {
            toast({
              variant: "destructive",
              title: "Authentication Error",
              description: "There was an authentication issue. Please refresh the page and try again."
            });
            onClose();
            return;
          }
          
          throw error;
        }
      } else {
        // User has a display - use existing logic
        const displayIdNum = parseInt(displayId, 10);
        
        if (isNaN(displayIdNum) || displayIdNum <= 0) {
          toast({
            variant: "destructive",
            title: "Invalid Display",
            description: "There was an error with your display. Please try refreshing the page."
          });
          onClose();
          return;
        }

        // Get or create the display year for the actual display using our utility
        try {
          const displayYear = await upsertDisplayYear(displayIdNum, values.year, `Songs from ${values.year}`);
          
          if (!displayYear) {
            throw new Error('Failed to get or create display year');
          }
          
          displayYearId = displayYear.id;
        } catch (error) {
          console.error('Error handling display year:', error);
          
          // Handle specific authentication errors
          if (error instanceof Error && (error.message?.includes('406') || error.message?.includes('Not Acceptable'))) {
            toast({
              variant: "destructive",
              title: "Authentication Error",
              description: "There was an authentication issue. Please refresh the page and try again."
            });
            onClose();
            return;
          }
          
          throw error;
        }
      }

      // Upload album cover if provided
      let albumCoverUrl = previewUrl;
      if (values.albumCover instanceof File) {
        albumCoverUrl = await uploadAlbumCover(values.albumCover);
      } else if (editingSong && !albumCoverUrl) {
        // Preserve existing album cover when editing
        albumCoverUrl = editingSong.albumCover || editingSong.album_cover_url || null;
      }

      const songData = {
        title: values.title.trim(),
        artist: values.artist.trim(),
        year_introduced: values.year,
        display_year_id: displayYearId,
        sequence_available: false,
        duration: values.duration.trim(),
        genre: values.genre?.trim() || null,
        album_cover_url: albumCoverUrl,
        video_url: values.videoUrl?.trim() || null
      };

      let insertedSong;
      if (editingSong) {
        // Update existing song
        const { data: updatedSong, error: updateError } = await supabase
          .from('display_songs')
          .update(songData)
          .eq('id', editingSong.id)
          .select('*')
          .single();

        if (updateError) {
          // Handle specific authentication errors
          if (updateError.message?.includes('406') || updateError.message?.includes('Not Acceptable')) {
            toast({
              variant: "destructive",
              title: "Authentication Error",
              description: "There was an authentication issue. Please refresh the page and try again."
            });
            onClose();
            return;
          }
          throw updateError;
        }
        if (!updatedSong) throw new Error('Failed to update song');
        insertedSong = updatedSong;
      } else {
        // Insert new song
        const { data: newSong, error: insertError } = await supabase
          .from('display_songs')
          .insert([songData])
          .select('*')
          .single();

        if (insertError) {
          // Handle specific authentication errors
          if (insertError.message?.includes('406') || insertError.message?.includes('Not Acceptable')) {
            toast({
              variant: "destructive",
              title: "Authentication Error",
              description: "There was an authentication issue. Please refresh the page and try again."
            });
            onClose();
            return;
          }
          throw insertError;
        }
        if (!newSong) throw new Error('Failed to insert song');
        insertedSong = newSong;
      }

      // Auto-create video if video_url is provided
      if (values.videoUrl && insertedSong) {
        console.log('Creating video from display song URL:', values.videoUrl);
        const videoResult = await createVideoFromDisplaySong(
          parseInt(displayId), // Convert displayId string to number
          values.videoUrl,
          user.id,
          {
            title: insertedSong.title,
            artist: insertedSong.artist,
            genre: insertedSong.genre,
            year_introduced: insertedSong.year_introduced,
            album_cover_url: insertedSong.album_cover_url
          }
        );

        if (videoResult.success) {
          console.log('Video created successfully:', videoResult.videoId);
        } else {
          console.warn('Failed to create video:', videoResult.error);
        }
      }

      // Transform the database response to match the Song type
      const newSong: Song = {
        id: insertedSong.id,
        title: insertedSong.title,
        artist: insertedSong.artist,
        duration: insertedSong.duration || '0:00',
        year: insertedSong.year_introduced,
        genre: insertedSong.genre || undefined,
        albumCover: insertedSong.album_cover_url || undefined,
        videoUrl: insertedSong.video_url || undefined
      };

      onSongAdded(newSong);
      toast({
        variant: "default",
        title: "Success",
        description: editingSong ? 'Song updated successfully' : 'Song added successfully'
      });
      onClose();
      form.reset();
      setPreviewUrl(null);
      setSelectedSpotifyTrack(null);
    } catch (error) {
      console.error('Form submission error:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to save song';
      if (error instanceof Error) {
        if (error.message.includes('406') || error.message.includes('Not Acceptable')) {
          errorMessage = 'Authentication issue. Please refresh the page and try again.';
        } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMessage = 'You are not authorized to perform this action. Please log in again.';
        } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
          errorMessage = 'Access denied. Please check your permissions.';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      form.setValue('albumCover', file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    }
  };

  const handleRemoveImage = () => {
    form.setValue('albumCover', undefined);
    setPreviewUrl(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingSong ? 'Edit Song' : 'Add New Song'}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Spotify Search Section */}
          <SpotifySongSearch
            onTrackSelect={handleSpotifyTrackSelect}
            selectedTrack={selectedSpotifyTrack}
            onClear={handleSpotifyTrackClear}
            placeholder="Search for the song to auto-populate details..."
            label="Search Spotify for Song"
            showPreview={true}
            disabled={isUploading}
          />

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Song Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter song title" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="artist"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Artist</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter artist name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Duration</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., 3:45" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="genre"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Genre (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Rock, Pop, Classical" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="videoUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Light Show Video URL (Optional)</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="e.g., https://youtube.com/watch?v=..." 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="text-sm text-muted-foreground">
                    Add a link to your light show video. Supports YouTube, Vimeo, Facebook, TikTok, Instagram, Twitch, and Dailymotion.
                  </p>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="year"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Year First Added to Display</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      placeholder="Enter year" 
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <FormLabel>Album Cover (Optional)</FormLabel>
              <div className="flex items-center gap-4">
                {previewUrl ? (
                  <div className="relative">
                    <img
                      src={previewUrl}
                      alt="Album cover preview"
                      className="h-24 w-24 object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive hover:bg-destructive/90"
                      onClick={handleRemoveImage}
                    >
                      <X className="h-4 w-4 text-white" />
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <div className="h-24 w-24 rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-muted-foreground/50 transition-colors flex items-center justify-center">
                      <Plus className="h-8 w-8 text-muted-foreground/50" />
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isUploading}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUploading}>
                {isUploading && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {editingSong ? 'Save Changes' : 'Add Song'}
              </Button>
            </div>
          </form>
        </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddSongForm; 